import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { authService } from "@/services/authService";
import { safeStorage } from "@/services/safeStorage";

const ThemeContext = createContext(null);
const KEY = "pt_theme";
const DARK_ROUTES = ["/login", "/register", "/"];

export const ThemeProvider = ({ children }) => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [theme, setTheme] = useState(() => safeStorage.get(KEY) || "dark");
  const forceDark = DARK_ROUTES.includes(pathname) || !user;

  useEffect(() => {
    if (user && user.theme && user.theme !== theme) {
      setTheme(user.theme);
    }
    if (user === false) {
      safeStorage.remove(KEY);
      setTheme("dark");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, user?.id, user?.theme]);

  useEffect(() => {
    const light = theme === "light" && !forceDark;
    document.documentElement.classList.toggle("light", light);
    if (user) safeStorage.set(KEY, theme);
  }, [theme, forceDark, user]);

  const toggle = useCallback(() => {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    if (user) authService.setTheme(next).catch(() => {});
  }, [theme, user]);

  const value = useMemo(
    () => ({ theme: forceDark ? "dark" : theme, toggle }),
    [theme, forceDark, toggle],
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
